import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiUrls } from '../api-urls';

@Injectable({ providedIn: 'root' })
export class AppConfigService {

  private config: any = null;
  private isLoaded = false;
  companyConfiguration: any = null;

  constructor(private http: HttpClient) { }

  load(): Promise<void> {
    return Promise.all([
      firstValueFrom(this.http.get('/assets/config.json')),
      firstValueFrom(this.http.get('/assets/version.json')).catch(() => ({}))
    ])
      .then(([config, version]: any[]) => {
        this.config = { ...config, ...version };
        this.isLoaded = true;
        console.log('AppConfig loaded successfully', this.config);
      })
      .catch(err => {
        console.error('Failed to load app config', err);
        throw err;
      });
  }

  async loadCompanyConfiguration(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.http.get(this.masterBaseUrl + ApiUrls.CompanyConfiguration.GetCompanyConfig, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      );

      if (response?.data) {
        this.companyConfiguration = response.data;
      }
    } catch (err) {
      console.error('Failed to load company configuration', err);
      this.companyConfiguration = {};
    }
  }

  get companyLogo() {
    return this.companyConfiguration?.logoURL || this.config.logoURL || '';
  }

  // currency
  get currency() {
    return this.companyConfiguration?.currency || this.config.currency;
  }

  private getValue(key: string, defaultValue: any = null): any {
    if (!this.isLoaded || !this.config) {
      // Silence warnings for keys commonly accessed early
      const earlyKeys = ['production', 'bypassAuth', 'masterBaseUrl', 'apiUrl'];
      if (!earlyKeys.includes(key)) {
        console.warn(`${key} accessed BEFORE config loaded.`);
      }
      return defaultValue;
    }

    const value = this.config[key];
    return value !== undefined && value !== null ? value : defaultValue;
  }

  // ==================== Getters ====================
  get production(): boolean { return this.getValue('production', false); }
  get bypassAuth(): boolean { return this.getValue('bypassAuth', false); }

  get apiUrl(): string { return this.getValue('apiUrl', ''); }
  get apiUrl2(): string { return this.getValue('apiUrl2', ''); }
  get masterBaseUrl(): string { return this.getValue('masterBaseUrl', ''); }

  get portalUrl(): string { return this.getValue('portalUrl', ''); }
  get portalPageUrl(): string { return this.getValue('portalUrl', ''); }
  get env(): string { return this.getValue('env', ''); }

  // Domain of the SSO cookies written by the Hub, prefixed with env (e.g. .dev.markaziahub.com)
  get ssoCookieDomain(): string {
    const env = this.env;
    return env ? `.${env}.markaziahub.com` : '.markaziahub.com';
  }

  get googleMapKey(): string { return this.getValue('googleMapKey', ''); }
  get generalToken(): string { return this.getValue('generalToken', ''); }

  get allowedDomains(): string[] { return this.getValue('allowedDomains', []); }

  get staticAuth(): any {
    return this.bypassAuth ? this.getValue('staticAuth', null) : null;
  }

  get version(): string { return this.getValue('version', '1.0.0'); }

  get isReady(): boolean { return this.isLoaded; }
}